// @flow

import * as React from 'react';
import { Component } from 'react';
import styled from 'styled-components';
import { Text } from './Text';
import type { UIProviderContext } from './UIProvider';
import { UIContextPropType } from './UIProvider';

type Props = {
  id: string,
  onClick: (event: any) => ?boolean,
  children?: React.Node,
  disabled?: boolean,
};

const Wrapper = styled.span`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  align-items: center;
  margin-left: 2rem;
  margin-right: 2rem;
  white-space: nowrap;
  max-width: 13.375rem;
  overflow: hidden;
`;

const PrimaryTextColors = styled(Text)`
  color: ${(props) =>
    props.disabled
      ? props.theme.button.primary.disabledTextColor
      : props.theme.button.primary.textColor};
`;

const SecondaryTextColors = styled(Text)`
  color: ${(props) =>
    props.disabled
      ? props.theme.button.secondary.disabledTextColor
      : props.theme.button.secondary.textColor};
`;

const BaseButtonFactory = (TextComponent) => {
  return class UnstyledButton extends Component<Props> {
    context: UIProviderContext;

    static contextTypes = {
      dbui: UIContextPropType,
    };

    handleClick = (evt: SyntheticMouseEvent<HTMLButtonElement>) => {
      this.props.onClick(evt);
      this.context.dbui.registerAnalyticsEvent(this.props.id);
    };

    render() {
      const { children, ...props } = this.props;
      return (
        <button {...props} onClick={this.handleClick}>
          <TextComponent disabled={props.disabled}>
            <Wrapper>{children}</Wrapper>
          </TextComponent>
        </button>
      );
    }
  };
};

const BaseButton = styled(BaseButtonFactory(PrimaryTextColors))`
  padding: 0;
  min-width: 9rem;
  min-height: 3rem;
  border-radius: 2.5rem;
  font-size: 1rem;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  background-image: ${(props) => props.theme.button.primary.backgroundImage};
  border: 1px solid ${(props) => props.theme.button.primary.borderColor};
  &:hover {
    box-shadow: ${(props) => props.theme.button.primary.hoverShadow};
    background-image: ${(props) =>
      props.theme.button.primary.backgroundImageHover};
  }
  &:focus {
    outline: none;
  }
  &:active {
    background-color: ${(props) => props.theme.button.primary.pressedColor};
    background-image: none;
  }
  &:disabled {
    background-color: ${(props) => props.theme.button.primary.disabledColor};
    border-color: ${(props) => props.theme.actionColor};
    background-image: none;
    box-shadow: none;
  }
`;

export const Button = (props: Props) => <BaseButton {...props} />;

const BaseSecondaryButton = styled(BaseButtonFactory(SecondaryTextColors))`
  padding: 0;
  min-width: 9rem;
  min-height: 3rem;
  border-radius: 2.5rem;
  font-size: 1rem;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  background-image: ${(props) => props.theme.button.secondary.backgroundImage};
  border: 1px solid ${(props) => props.theme.button.secondary.borderColor};
  &:hover {
    box-shadow: ${(props) => props.theme.button.secondary.hoverShadow};
    background-image: ${(props) =>
      props.theme.button.secondary.backgroundImageHover};
    border: 1px solid
      ${(props) => props.theme.button.secondary.borderColorHover};
  }
  &:focus {
    outline: none;
  }
  &:active {
    background-color: ${(props) => props.theme.button.secondary.pressedColor};
    background-image: none;
  }
  &:disabled {
    background-color: ${(props) => props.theme.button.secondary.disabledColor};
    box-shadow: none;
    background-image: none;
    border: 1px solid
      ${(props) => props.theme.button.secondary.borderColorHover};
  }
`;

export const SecondaryButton = (props: Props) => (
  <BaseSecondaryButton {...props} />
);

const BaseDarkButton = styled(BaseButtonFactory(PrimaryTextColors))`
  padding: 0;
  min-width: 9rem;
  min-height: 3rem;
  border-radius: 2.5rem;
  font-size: 1rem;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  background-image: ${(props) => props.theme.button.dark.backgroundImage};
  border: 1px solid ${(props) => props.theme.button.dark.borderColor};
  &:hover {
    box-shadow: ${(props) => props.theme.button.dark.hoverShadow};
    background-image: ${(props) =>
      props.theme.button.dark.backgroundImageHover};
  }
  &:focus {
    outline: none;
  }
  &:active {
    background-color: ${(props) => props.theme.button.dark.pressedColor};
    background-image: none;
  }
  &:disabled {
    background-color: ${(props) => props.theme.button.dark.disabledColor};
    border-color: ${(props) => props.theme.button.dark.pressedColor};
    background-image: none;
    box-shadow: none;
  }
`;

export const DarkButton = (props: Props) => <BaseDarkButton {...props} />;

const BaseBorderlessButton = styled(BaseButtonFactory(SecondaryTextColors))`
  padding: 0;
  background-color: transparent;
  font-size: 1rem;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  border: none;
`;

export const BorderlessButton = (props: Props) => (
  <BaseBorderlessButton {...props} />
);
